import { Component, ViewChild } from '@angular/core';
import { LazyLoadEvent } from 'primeng/api';
import { AccountService } from 'src/app/core/services/account.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { Table } from 'primeng/table';
import { FilterValues } from 'src/app/shared/components/filter/filter.component';
import { S3SetupPayload, S3StorageService } from 'src/app/core/services/s3-storage.service';
import { ConfirmModalService } from 'src/app/core/services/confirm-modal.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { normalizeApiValidationError } from 'src/app/shared/utils/api-validation.util';
@Component({
    selector: 'app-s3-storage',
    templateUrl: './s3-storage.component.html',
    styleUrl: './s3-storage.component.scss',
    standalone: false
})
export class S3StorageComponent {

  loading: boolean = false;
  documents: any = [];
  customerProfile: any = {};
  s3SetupForm: FormGroup;
  showS3SetupModal = false;
  s3SetupSubmitted = false;
  s3SetupLoading = false;
  showSecretAccessKey = false;

  constructor(
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private accountService: AccountService,
    private s3Service: S3StorageService,
    private confirmModalService: ConfirmModalService
  ) {
    this.s3SetupForm = this.formBuilder.group({
      access_key_id: ['', [Validators.required, Validators.pattern(/\S/)]],
      secret_access_key: ['', [Validators.required, Validators.pattern(/\S/)]],
      bucket: ['', [Validators.required, Validators.pattern(/\S/)]],
      region: ['', [Validators.required, Validators.pattern(/\S/)]],
      folder: ['', [Validators.required, Validators.pattern(/\S/)]]
    });
  }

  ngOnInit() {
    this.getCustomerProfile();
  }

  getCustomerProfile() {
    this.loading = true;
    this.accountService.getCustomerProfile().subscribe({
      next: (res: any) => {
        this.loading = false;
        this.customerProfile = res;
        this.isS3Configured = Object.keys(res).includes('s3Bucket');
      },
      error: (err) => {
        this.loading = false;
      }
    });
  }

  getS3Documents(event: LazyLoadEvent) {
    Promise.resolve().then(() => this.loading = true);
    const first = event?.first ?? 0;
    const rows = event?.rows ?? this.rows;
    const page = first / rows + 1;
    let startDate: string | null = null;
    let endDate: string | null = null;

    if (this.rangeDates && this.rangeDates.length === 2) {
      startDate = this.formatDate(this.rangeDates[0]);
      endDate = this.formatDate(this.rangeDates[1]);
    }

    const queryParams: any = {
      page: page,
      limit: rows,
      search: this.searchText,
      startDate: startDate,
      endDate: endDate
    };

    this.s3Service.getS3Documents(queryParams).subscribe(
      (res: any) => {
        this.loading = false;
        this.documents = res?.data || [];
        this.totalRecords = res?.totalCount || 0;
      },
      (err) => {
        this.loading = false;
      }
    );
  }

  formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  }



  totalRecords = 0;
  rows = 10;
  searchText: any = '';
  rangeDates: Date[] | null = null;
  @ViewChild('dt') table!: Table;

  onFilterChange(values: FilterValues) {
    this.searchText = values.search;
    this.rangeDates = values.dateRange;
    this.table.reset();
  }

  isS3Configured: boolean = false;

  connectS3Bucket(): void {
    this.loading = true;

    this.s3Service.connectS3Bucket().subscribe({
      next: (res: any) => {
        this.loading = false;
        this.getCustomerProfile();
        this.toastService.showToaster("Connected", res?.message, "success");

      },

      error: (err: any) => {
        this.loading = false;
      }
    });
  }
  toggleS3BucketStatus(event: Event) {
    const input = event.target as HTMLInputElement;
    const checked = input.checked;

    const oldStatus = !!this.customerProfile?.s3Bucket?.isS3Connected;
    input.checked = oldStatus;

    const title = checked ? 'Activate S3 Bucket' : 'Deactivate S3 Bucket';
    const message = checked
      ? 'Are you sure you want to activate S3 Bucket integration?'
      : 'Are you sure you want to deactivate S3 Bucket integration?';

    this.confirmModalService.open(
      title,
      message,
      () => {
        this.confirmS3BucketStatusChange(checked);
      },
      checked ? 'Activate' : 'Deactivate',
      'Cancel'
    );
  }

  confirmS3BucketStatusChange(status: boolean) {
    if (status) {
      this.connectS3Bucket();
      return;
    }

    this.deactivateS3Bucket();
  }

  deactivateS3Bucket() {
    this.toastService.showToaster(
      'S3 Bucket',
      'This feature is under development.',
      'info'
    );

    this.getCustomerProfile();

  }

  get s3SetupControls() {
    return this.s3SetupForm.controls;
  }

  openS3SetupModal(): void {
    this.s3SetupSubmitted = false;
    this.showSecretAccessKey = false;
    this.s3SetupForm.reset({
      access_key_id: '',
      secret_access_key: '',
      bucket: '',
      region: '',
      folder: ''
    });
    this.showS3SetupModal = true;
  }

  closeS3SetupModal(): void {
    if (this.s3SetupLoading) {
      return;
    }

    this.showS3SetupModal = false;
    this.s3SetupSubmitted = false;
    this.showSecretAccessKey = false;
    this.s3SetupForm.reset();
  }

  toggleSecretAccessKeyVisibility(): void {
    this.showSecretAccessKey = !this.showSecretAccessKey;
  }

  setupS3Bucket(): void {
    this.s3SetupSubmitted = true;
    this.s3SetupForm.markAllAsTouched();

    if (this.s3SetupForm.invalid || this.s3SetupLoading) {
      return;
    }

    const formValue = this.s3SetupForm.getRawValue();
    const payload: S3SetupPayload = {
      access_key_id: formValue.access_key_id.trim(),
      secret_access_key: formValue.secret_access_key.trim(),
      bucket: formValue.bucket.trim(),
      region: formValue.region.trim(),
      folder: formValue.folder.trim()
    };

    this.s3SetupLoading = true;

    this.s3Service.setupS3Bucket(payload).subscribe({
      next: (res: any) => {
        this.s3SetupLoading = false;
        this.toastService.showToaster(
          'S3 Setup',
          res?.message || 'S3 bucket configured successfully.',
          'success'
        );
        this.closeS3SetupModal();
        this.getCustomerProfile();
      },

      error: (error: unknown) => {
        this.s3SetupLoading = false;
        const apiError = normalizeApiValidationError(error);
        this.toastService.showToaster('S3 Setup Failed', apiError.message, 'error');
      }
    });
  }

  downloadS3Document(doc: any) {
    const documentId = doc.id;
    if (!documentId) {
      console.error('No document id found');
      return;
    }

    this.s3Service.downloadS3Document(documentId).subscribe({

      next: (response: Blob) => {
        if (!response || response.size === 0) {
          this.toastService.showToaster("Failed", "Downloaded file is empty", "error");
          return;
        }

        const url = window.URL.createObjectURL(response);
        const a = document.createElement('a');
        a.href = url;

        const baseName = doc.original_filename || doc.name || `document-${documentId}`;
        a.download = baseName.toLowerCase().endsWith('.pdf') ? baseName : `${baseName}.pdf`;

        document.body.appendChild(a);
        a.click();

        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 100);

        this.toastService.showToaster("Success", "Document downloaded successfully", "success");
      },

      error: err => {
        this.toastService.showToaster("Failed", "Unable to download document", "error");
      }
    });
  }




}


